// The build journey strip: the same seven steps on every boat (GRP → Assembly → Engine → Start-up →
// Sea trial → PDI → Delivery). Shape carries the state as well as colour: done is a low green bar, the
// current step a raised blue bar, a hold a raised amber hatch, a late step a raised red bar, and a step
// not started yet a low track. The state comes from `journeyOf` in @technohull/shared, so the strip
// always agrees with the production board.
//
//   <JourneyStrip vessel={v} />                       a row's strip (md)
//   <JourneyStrip vessel={v} size="sm" />             dense lists, search results
//   <JourneyStrip steps={steps} size="lg" labels />   a boat header, each step named under its bar
//   <JourneyLegend />                                 the key, for a page or panel header
//
// Styles: the "Journey strip" section of styles/global.css.

import { useMemo } from 'react';
import { journeyOf, JOURNEY_STAGES, type JourneyState, type JourneyStep, type OverviewVessel } from '@technohull/shared';

export type JourneySize = 'sm' | 'md' | 'lg';

export interface JourneyStripProps {
  /** the boat; its steps come from journeyOf(vessel). Ignored when `steps` is given. */
  vessel?: OverviewVessel;
  /** precomputed steps (e.g. journeyOf(v, now) shared with a row's other cells). */
  steps?: JourneyStep[];
  /** sm 8px segments, md 10px (default), lg fills its container. */
  size?: JourneySize;
  /** name each step under its segment (the strip then fills its container's width). */
  labels?: boolean;
  className?: string;
  /** the strip in words when it isn't a build journey (e.g. a boat's service journey). */
  summary?: string;
  /** a step's state in words for its tooltip, when it isn't a build journey. */
  stepWord?: (step: JourneyStep, index: number) => string;
}

const LAST = JOURNEY_STAGES.length - 1;

/** one step's state in words, for its tooltip ("Sea trial: on hold"). */
export function journeyStateWord(step: JourneyStep, index: number): string {
  switch (step.state) {
    case 'done': return 'done';
    case 'current': return index === LAST ? 'next' : 'in progress';
    case 'hold': return 'on hold';
    case 'late': return index === LAST ? 'late' : 'over target';
    default: return 'not started';
  }
}

/** the whole strip in one sentence, for its accessible name and for a row's title:
 *  "Build journey: Assembly in progress, 1 of 7 done, delivery late". */
export function journeySummary(steps: JourneyStep[]): string {
  const total = steps.length;
  const done = steps.filter((s) => s.state === 'done').length;
  if (total && done === total) return `Build journey: all ${total} steps done`;
  const at = steps.findIndex((s) => s.state === 'current' || s.state === 'hold' || s.state === 'late');
  if (at < 0) return 'Build journey: not started';
  const step = steps[at]!;
  const last = total - 1;
  let where: string;
  if (at === last) {
    where = step.state === 'late' ? 'Delivery overdue' : step.state === 'hold' ? 'Delivery on hold' : 'Ready for delivery';
  } else {
    where = `${step.label} ${journeyStateWord(step, at)}`;
  }
  const parts = [where, `${done} of ${total} done`];
  if (at < last && steps[last]?.state === 'late') parts.push('delivery late');
  return `Build journey: ${parts.join(', ')}`;
}

export function JourneyStrip({ vessel, steps, size = 'md', labels = false, className, summary, stepWord = journeyStateWord }: JourneyStripProps) {
  const list = useMemo(() => steps ?? (vessel ? journeyOf(vessel) : null), [steps, vessel]);
  if (!list || list.length === 0) return null;
  const cls = `journey journey--${size}${labels ? ' journey--labelled' : ''}${className ? ` ${className}` : ''}`;
  return (
    <span className={cls} role="img" aria-label={summary ?? journeySummary(list)}>
      {list.map((s, i) => (
        <span key={s.key} className="journey-step" title={`${s.label}: ${stepWord(s, i)}`}>
          <i className="journey-seg" data-state={s.state} />
          {labels && <span className="journey-lbl">{s.label}</span>}
        </span>
      ))}
    </span>
  );
}

const LEGEND: Array<{ state: JourneyState; label: string }> = [
  { state: 'done', label: 'Done' },
  { state: 'current', label: 'Current' },
  { state: 'hold', label: 'Hold' },
  { state: 'late', label: 'Late' },
  { state: 'todo', label: 'Not started' },
];

/** the strip's key, sized for a page or panel header. */
export function JourneyLegend({ className }: { className?: string }) {
  return (
    <ul className={`journey-key${className ? ` ${className}` : ''}`} aria-label="Build journey key">
      {LEGEND.map((l) => (
        <li key={l.state}>
          <i className="journey-seg" data-state={l.state} aria-hidden="true" />
          {l.label}
        </li>
      ))}
    </ul>
  );
}
